/**
 * Date utility functions for consistent date handling across the application.
 */

/**
 * Formats a date string for display.
 *
 * IMPORTANT: This function handles date-only strings (e.g., "2025-12-16") by parsing
 * them as local dates rather than UTC. This prevents the off-by-one day bug that occurs
 * when JavaScript's Date constructor interprets date-only strings as UTC midnight,
 * which then shifts to the previous day when converted to local time in timezones
 * behind UTC.
 *
 * @param dateString - A date string in YYYY-MM-DD format or ISO format
 * @returns Formatted date string (e.g., "Dec 16, 2025") or "-" if invalid
 */
export const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return "-";

  // Extract just the date portion (handles both "2025-12-16" and "2025-12-16T00:00:00Z")
  const datePart = dateString.split("T")[0];
  const [year, month, day] = datePart.split("-").map(Number);

  // Validate parsed values
  if (isNaN(year) || isNaN(month) || isNaN(day)) return "-";

  // Create date using local timezone (month is 0-indexed)
  const date = new Date(year, month - 1, day);

  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

/**
 * Formats a date string with time for display.
 *
 * @param dateString - A date string in ISO format
 * @returns Formatted date and time string (e.g., "Dec 16, 2025, 3:45 PM") or "-" if invalid
 */
export const formatDateTime = (dateString: string | null | undefined): string => {
  if (!dateString) return "-";

  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "-";

  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};
